import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';

// ── Shelly ───────────────────────────────────────────────────────────────────

export interface ShellyCloudConfig {
    server_uri: string | null;
    auth_key: string | null;        // masked on read (e.g. "abc123…ef90")
    auth_key_set: boolean;
    poll_interval_seconds: number;
    enabled: boolean;
    last_poll_at: string | null;
    last_error: string | null;
}

export interface ShellyDevice {
    id: number;
    device_id: string;
    name: string;
    enabled: boolean;
    last_seen: string | null;
    created_at: string;
}

export interface ShellyCloudDevice {
    id: string;
    gen: string;
    code: string;
    online: boolean;
}

export const fetchShellyConfig = async (): Promise<ShellyCloudConfig> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/shelly/config`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta Shelly-inställningar'); }
};

export const saveShellyConfig = async (cfg: Partial<{
    server_uri: string;
    auth_key: string;
    poll_interval_seconds: number;
    enabled: boolean;
}>): Promise<any> => {
    try {
        const res = await axios.post(`${API_BASE_URL}/shelly/config`, cfg);
        return res.data;
    } catch (error) { return handleApiError(error, 'Spara Shelly-inställningar'); }
};

export const fetchShellyCloudDevices = async (): Promise<ShellyCloudDevice[]> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/shelly/cloud-devices`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta Shelly-enheter från molnet'); }
};

export const fetchShellyDevices = async (): Promise<ShellyDevice[]> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/shelly/devices`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta Shelly-enheter'); }
};

export const addShellyDevice = async (device_id: string, name: string): Promise<{ device_id: string; name: string }> => {
    try {
        const res = await axios.post(`${API_BASE_URL}/shelly/devices`, { device_id, name });
        return res.data;
    } catch (error) { return handleApiError(error, 'Lägga till Shelly-enhet'); }
};

export const updateShellyDevice = async (deviceId: string, updates: { name?: string; enabled?: boolean }): Promise<any> => {
    try {
        const res = await axios.patch(`${API_BASE_URL}/shelly/devices/${deviceId}`, updates);
        return res.data;
    } catch (error) { return handleApiError(error, 'Spara Shelly-enhet'); }
};

export const removeShellyDevice = async (deviceId: string): Promise<any> => {
    try {
        const res = await axios.delete(`${API_BASE_URL}/shelly/devices/${deviceId}`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Ta bort Shelly-enhet'); }
};

export const triggerShellyPollNow = async (): Promise<{ message: string }> => {
    try {
        const res = await axios.post(`${API_BASE_URL}/shelly/poll-now`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta från Shelly nu'); }
};

export interface ShellyReading {
    key: string;
    name: string;
    unit: string;
    enumValues: { value: string; text: string }[] | null;
    valueNum: number | null;
    valueStr: string | null;
    timestamp: string;
}

export const fetchShellyDeviceReadings = async (deviceId: string): Promise<ShellyReading[]> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/shelly/devices/${encodeURIComponent(deviceId)}/readings`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta värden från Shelly-enhet'); }
};
